import { useEffect, useRef, useState } from '@wordpress/element';

export function useCastPlayer({
    audioRef,
    currentTrack,
    hasNext,
    next,
    onProgress,
    onEnded,
    onBrowserResume,
}) {
    const castSessionRef = useRef(null);
    const castMediaSessionRef = useRef(null);
    const castLoadingRef = useRef(false);
    const castEndedHandledRef = useRef(false);
    const castProgressTimerRef = useRef(null);
    const lastLoadedTrackIdRef = useRef(null);
    const castIsPlayingRef = useRef(false);
    const pendingCastTrackRef = useRef(null);
    const currentTrackRef = useRef(currentTrack);
    const castInitializedRef = useRef(false);
    const forceFreshSessionRef = useRef(false);
    const latestPlaybackTimeRef = useRef(0);

    const [castAvailable, setCastAvailable] = useState(false);
    const [castConnected, setCastConnected] = useState(false);
    const [castIsPlaying, setCastIsPlaying] = useState(false);

    useEffect(() => {
        currentTrackRef.current = currentTrack;
    }, [currentTrack]);

    function setRemotePlaying(value) {
        castIsPlayingRef.current = value;
        setCastIsPlaying(value);
    }

    function formatTime(seconds) {
        if (isNaN(seconds) || !isFinite(seconds)) return '0:00';

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }

    function getCastContext() {
        return window.cast?.framework?.CastContext?.getInstance?.();
    }

    function getCastSession() {
        return getCastContext()?.getCurrentSession?.() || castSessionRef.current;
    }

    function getCastMediaSession() {
        const session = getCastSession();
        return session?.getMediaSession?.() || castMediaSessionRef.current;
    }

    function getCastTimeInfo(mediaSession = getCastMediaSession()) {
        if (!mediaSession) {
            return { currentTime: 0, duration: 0 };
        }

        return {
            currentTime: Number(mediaSession.getEstimatedTime?.() || 0),
            duration: Number(mediaSession.media?.duration || 0),
        };
    }

    function stopCastProgressTimer() {
        if (castProgressTimerRef.current) {
            window.clearInterval(castProgressTimerRef.current);
            castProgressTimerRef.current = null;
        }
    }

    function syncCastProgress(mediaSession = getCastMediaSession()) {
        const { currentTime, duration } = getCastTimeInfo(mediaSession);

        if (!duration) return;

        const percent = Math.min(100, Math.max(0, (currentTime / duration) * 100));

        onProgress?.({
            percent,
            current: formatTime(currentTime),
            duration: formatTime(duration),
            currentTime,
            durationSeconds: duration,
        });

        if (
            audioRef.current &&
            Math.abs(audioRef.current.currentTime - currentTime) > 0.35
        ) {
            audioRef.current.currentTime = currentTime;
        }

        if (currentTime >= duration - 1.25 && !castEndedHandledRef.current) {
            handleCastEnded();
        }
    }

    function startCastProgressTimer() {
        stopCastProgressTimer();

        castProgressTimerRef.current = window.setInterval(() => {
            const latestMediaSession = getCastMediaSession();
            syncCastProgress(latestMediaSession);
        }, 500);
    }

    function handleCastEnded() {
        if (castEndedHandledRef.current) return;

        castEndedHandledRef.current = true;
        setRemotePlaying(false);

        onEnded?.();

        if (hasNext) {
            next();
            return;
        }
        stopCastProgressTimer();
    }

    function syncCastMediaSession(mediaSession) {
        if (!mediaSession?.media) {
            castMediaSessionRef.current = null;
            setRemotePlaying(false);
            stopCastProgressTimer();
            return;
        }

        castMediaSessionRef.current = mediaSession;

        function syncState() {
            const isPlaying =
                mediaSession.playerState === chrome.cast.media.PlayerState.PLAYING ||
                mediaSession.playerState === chrome.cast.media.PlayerState.BUFFERING;

            setRemotePlaying(isPlaying);
            syncCastProgress(mediaSession);

            if (
                mediaSession.playerState === chrome.cast.media.PlayerState.IDLE &&
                mediaSession.idleReason === chrome.cast.media.IdleReason.FINISHED
            ) {
                handleCastEnded();
            }
        }

        syncState();
        startCastProgressTimer();

        mediaSession.addUpdateListener((isAlive) => {
            if (!isAlive) {
                castMediaSessionRef.current = null;
                setRemotePlaying(false);
                stopCastProgressTimer();
                return;
            }

            syncState();
        });
    }

    async function forceRemotePlay(mediaSession) {
        if (!mediaSession?.play) return;

        try {
            await mediaSession.play(new chrome.cast.media.PlayRequest());
            setRemotePlaying(true);
        } catch (error) {
            // Ignore. Receiver may already be playing.
        }
    }

    async function loadTrackToCast(
        track,
        { autoplay = true, force = false, startTime = null } = {}
    ) {
        if (!track?.audioUrl || !window.cast?.framework || !window.chrome?.cast) {
            return null;
        }

        if (castLoadingRef.current) {
            return null;
        }

        if (!force && lastLoadedTrackIdRef.current === track.id) {
            const existingMediaSession = getCastMediaSession();

            if (existingMediaSession?.media) {
                await forceRemotePlay(existingMediaSession);
                return existingMediaSession;
            }
        }

        const session = getCastSession();

        if (!session) {
            pendingCastTrackRef.current = track;
            return null;
        }

        try {
            castLoadingRef.current = true;
            castEndedHandledRef.current = false;

            const mediaInfo = new chrome.cast.media.MediaInfo(
                track.audioUrl,
                'audio/mpeg'
            );

            mediaInfo.metadata = new chrome.cast.media.MusicTrackMediaMetadata();
            mediaInfo.metadata.title = track.title || 'Bajantube Track';
            mediaInfo.metadata.artist = track.artist || 'De Crop Over Larder';
            mediaInfo.metadata.albumName = 'De Crop Over Larder';

            if (track.cover) {
                mediaInfo.metadata.images = [
                    new chrome.cast.Image(track.cover),
                ];
            }

            const request = new chrome.cast.media.LoadRequest(mediaInfo);
            request.autoplay = autoplay;

            const browserTime = Number(audioRef.current?.currentTime || 0);
            const nextStartTime = startTime !== null ? startTime : browserTime;

            request.currentTime = nextStartTime > 1 ? nextStartTime : 0;

            const mediaSession = await session.loadMedia(request);
            const nextMediaSession = mediaSession || session.getMediaSession?.();

            lastLoadedTrackIdRef.current = track.id;
            castMediaSessionRef.current = nextMediaSession;

            audioRef.current?.pause();

            setCastConnected(true);
            if (autoplay) {
                setRemotePlaying(true);
            }
            syncCastMediaSession(nextMediaSession);

            startCastProgressTimer();

            window.setTimeout(() => {
                syncCastProgress(getCastMediaSession());
            }, 300);

            if (autoplay) {
                await forceRemotePlay(nextMediaSession);

                window.setTimeout(() => {
                    forceRemotePlay(getCastMediaSession());
                }, 700);
            }

            return nextMediaSession;
        } catch (error) {
            console.error('Failed to load track to Cast:', error);
            return null;
        } finally {
            castLoadingRef.current = false;
        }
    }

    async function castCurrentTrack(trackOverride = null) {
        const track = trackOverride?.audioUrl ? trackOverride : currentTrack;

        if (!track?.audioUrl) return null;

        pendingCastTrackRef.current = track;

        const context = getCastContext();

        if (!context) return null;

        let session = forceFreshSessionRef.current
            ? null
            : context.getCurrentSession();

        if (!session) {
            try {
                await new Promise((resolve) => window.setTimeout(resolve, 500));
                session = await context.requestSession();
                forceFreshSessionRef.current = false;
            } catch (error) {
                console.error('Failed to request Cast session:', error);
                return null;
            }
        }

        castSessionRef.current = session;
        setCastConnected(true);
        setRemotePlaying(true);

        return loadTrackToCast(track, {
            autoplay: true,
            force: true,
        });
    }

    async function stopCasting({ continueInBrowser = true } = {}) {
        const mediaSession = getCastMediaSession();
        const { currentTime } = getCastTimeInfo(mediaSession);
        const wasPlaying = castIsPlayingRef.current;

        stopCastProgressTimer();

        try {
            if (continueInBrowser && audioRef.current && currentTime > 0) {
                audioRef.current.currentTime = currentTime;
            }

            const session = getCastSession();

            if (session) {
                await session.endSession(true);
                await new Promise((resolve) => window.setTimeout(resolve, 500));
            }
        } catch (error) {
            console.error('Failed to stop casting:', error);
        } finally {
            castLoadingRef.current = false;
            castSessionRef.current = null;
            castMediaSessionRef.current = null;
            lastLoadedTrackIdRef.current = null;
            castEndedHandledRef.current = false;
            pendingCastTrackRef.current = null;
            forceFreshSessionRef.current = true;

            setCastConnected(false);
            setRemotePlaying(false);

            if (continueInBrowser && wasPlaying && audioRef.current) {
                onBrowserResume?.();

                window.setTimeout(() => {
                    audioRef.current.play().catch(() => { });
                }, 150);
            }
        }
    }

    async function toggleCast() {
        if (!currentTrack?.audioUrl) {
            return;
        }

        const mediaSession = getCastMediaSession();
        const hasLoadedMedia = Boolean(mediaSession?.media);
        const isCurrentTrackLoaded =
            hasLoadedMedia &&
            lastLoadedTrackIdRef.current === currentTrack.id;

        if (castConnected || isCurrentTrackLoaded) {
            await stopCasting({ continueInBrowser: true });
            return;
        }

        castLoadingRef.current = false;

        await castCurrentTrack();
    }
    async function toggleCastPlayback(shouldPlay = null) {
        let mediaSession = getCastMediaSession();

        if (!mediaSession?.media) {
            mediaSession = await castCurrentTrack();
            return;
        }

        try {
            const isRemotePlaying =
                mediaSession.playerState === chrome.cast.media.PlayerState.PLAYING ||
                mediaSession.playerState === chrome.cast.media.PlayerState.BUFFERING ||
                castIsPlayingRef.current;

            const nextShouldPlay =
                shouldPlay === null ? !isRemotePlaying : Boolean(shouldPlay);

            if (nextShouldPlay) {
                await mediaSession.play(new chrome.cast.media.PlayRequest());
                setRemotePlaying(true);
            } else {
                await mediaSession.pause(new chrome.cast.media.PauseRequest());
                setRemotePlaying(false);
            }
        } catch (error) {
            console.error('Failed to control Cast playback:', error);
        }
    }

    function seekCast(ratio) {
        const mediaSession = getCastMediaSession();
        const duration = Number(mediaSession?.media?.duration || 0);

        if (!mediaSession || !duration) return;

        const seekRequest = new chrome.cast.media.SeekRequest();
        seekRequest.currentTime = duration * ratio;

        mediaSession.seek(seekRequest).then(
            () => syncCastProgress(mediaSession),
            (error) => console.error('Failed to seek Cast playback:', error)
        );
    }

    useEffect(() => {
        window.__onGCastApiAvailable = function (isAvailable) {
            if (!isAvailable || !window.cast?.framework || !window.chrome?.cast) {
                return;
            }

            const context = getCastContext();

            context.setOptions({
                receiverApplicationId:
                    chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
                autoJoinPolicy:
                    chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });

            setCastAvailable(true);

            context.addEventListener(
                cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
                (event) => {
                    const isConnected =
                        event.sessionState === cast.framework.SessionState.SESSION_STARTED ||
                        event.sessionState === cast.framework.SessionState.SESSION_RESUMED;

                    if (isConnected) {
                        castSessionRef.current = event.session;
                        setCastConnected(true);
                        audioRef.current?.pause();

                        const mediaSession = event.session?.getMediaSession?.();

                        if (mediaSession?.media) {
                            syncCastMediaSession(mediaSession);
                            return;
                        }

                        const pendingTrack = pendingCastTrackRef.current || currentTrackRef.current;

                        if (pendingTrack?.audioUrl) {
                            window.setTimeout(() => {
                                loadTrackToCast(pendingTrack, {
                                    autoplay: true,
                                    force: true,
                                }).catch(() => { });
                            }, 500);
                        }
                    }

                    if (
                        event.sessionState === cast.framework.SessionState.SESSION_ENDED ||
                        event.sessionState === cast.framework.SessionState.SESSION_ENDING
                    ) {
                        castSessionRef.current = null;
                        castMediaSessionRef.current = null;
                        lastLoadedTrackIdRef.current = null;
                        pendingCastTrackRef.current = null;

                        setCastConnected(false);
                        setRemotePlaying(false);
                        stopCastProgressTimer();
                    }
                }
            );
        };

        return () => {
            stopCastProgressTimer();
        };
    }, []);

    useEffect(() => {
        if (!castConnected || !currentTrack?.audioUrl) {
            return;
        }

        if (lastLoadedTrackIdRef.current === currentTrack.id) {
            return;
        }

        loadTrackToCast(currentTrack, {
            autoplay: true,
            force: true,
            startTime: 0,
        }).catch(() => { });
    }, [castConnected, currentTrack?.id, currentTrack?.audioUrl]);

    return {
        castAvailable,
        castConnected,
        castIsPlaying,
        castCurrentTrack,
        toggleCast,
        stopCasting,
        toggleCastPlayback,
        seekCast,
    };
}